import React, { useState, useEffect } from 'react';
import UserManagement from './UserManagement';
import MailSystemSettings from './MailSystemSettings';
import LeaveApprovalManagement from './LeaveApprovalManagement';
import OrganizationManagement from './OrganizationManagement';
import LeavePolicyManagement from './LeavePolicyManagement';
import UsageAnalytics from './UsageAnalytics';
import { 
  Users, 
  Mail, 
  ShieldCheck, 
  GitBranch, 
  CalendarClock, 
  BarChart3, 
  Settings2, 
  ArrowUp, 
  ArrowDown, 
  Edit2, 
  Check, 
  X, 
  RotateCcw 
} from 'lucide-react';

const DEFAULT_CATEGORIES = [
  { id: 'analytics', label: '임직원 이용 통계 지표', iconName: 'BarChart3' },
  { id: 'users', label: '직원 및 계정 관리', iconName: 'Users' },
  { id: 'org', label: '조직도 & 결재선 관리', iconName: 'GitBranch' },
  { id: 'policy', label: '연차정책 관리', iconName: 'CalendarClock' },
  { id: 'leave', label: '연차 & 비용 결재관리', iconName: 'ShieldCheck' },
  { id: 'mail', label: '메일 및 시스템 설정', iconName: 'Mail' }
];

const AdminDashboard = () => {
  const [activeAdminSubTab, setActiveAdminSubTab] = useState('analytics');
  
  // Custom category configuration state
  const [categories, setCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('clou_admin_categories_v2');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {
      console.warn('Failed to parse admin categories:', e);
    }
    return DEFAULT_CATEGORIES;
  });

  const [isEditingCategories, setIsEditingCategories] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [tempLabel, setTempLabel] = useState('');

  // Persist to localStorage
  const saveCategories = (newCats) => {
    setCategories(newCats);
    localStorage.setItem('clou_admin_categories_v2', JSON.stringify(newCats));
  };

  const handleMoveCategory = (index, direction) => {
    const targetIndex = direction === 'UP' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= categories.length) return;
    const updated = [...categories];
    const [moved] = updated.splice(index, 1);
    updated.splice(targetIndex, 0, moved);
    saveCategories(updated);
  };

  const handleStartRename = (cat) => {
    setEditingCategoryId(cat.id);
    setTempLabel(cat.label);
  };

  const handleSaveRename = (catId) => {
    if (!tempLabel.trim()) return;
    const updated = categories.map(c => c.id === catId ? { ...c, label: tempLabel.trim() } : c);
    saveCategories(updated);
    setEditingCategoryId(null);
    setTempLabel('');
  };

  const handleResetDefault = () => {
    if (window.confirm('관리자 메뉴 카테고리 이름 및 순서를 기본값으로 초기화하시겠습니까?')) {
      saveCategories(DEFAULT_CATEGORIES);
      setEditingCategoryId(null);
    }
  };

  const renderIcon = (iconName) => {
    switch (iconName) {
      case 'BarChart3': return <BarChart3 className="w-4 h-4" />;
      case 'Users': return <Users className="w-4 h-4" />;
      case 'GitBranch': return <GitBranch className="w-4 h-4 text-indigo-600" />;
      case 'CalendarClock': return <CalendarClock className="w-4 h-4 text-cyan-600" />;
      case 'ShieldCheck': return <ShieldCheck className="w-4 h-4" />;
      case 'Mail': return <Mail className="w-4 h-4" />;
      default: return <ShieldCheck className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] bg-slate-50 text-slate-800">
      {/* Dashboard Top Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-2xs">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-50 text-indigo-600 p-2 rounded-xl border border-indigo-200">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="text-left">
            <h1 className="text-xl font-bold text-slate-900">통합 관리자 콘솔</h1>
            <p className="text-xs text-slate-500 mt-0.5">임직원 이용 통계 지표, 인사 정보, 조직도 및 결재선, 연차 정책, 메일 시스템을 통합 관리합니다.</p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start md:self-auto">
          <button
            onClick={() => setIsEditingCategories(!isEditingCategories)}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              isEditingCategories ? 'bg-amber-600 text-white shadow-xs' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300'
            }`}
          >
            <Settings2 className="w-3.5 h-3.5" />
            <span>{isEditingCategories ? '편집 완료' : '메뉴 카테고리 관리/순서 변경'}</span>
          </button>

          <span className="text-[10px] text-indigo-700 font-bold tracking-wider bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-lg uppercase">
            Admin Panel
          </span>
        </div>
      </div>

      <div className="p-6 md:p-8 flex flex-col md:flex-row gap-6">
        {/* Left Side Tab Navigation */}
        <div className="w-full md:w-64 space-y-2 flex-shrink-0 text-left">
          <div className="flex items-center justify-between px-3 mb-2">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
              메인 카테고리 {isEditingCategories && '(편집 모드)'}
            </span>
            {isEditingCategories && (
              <button
                onClick={handleResetDefault}
                className="text-[10px] text-amber-700 font-bold hover:underline flex items-center gap-0.5"
                title="기본 카테고리로 초기화"
              >
                <RotateCcw className="w-3 h-3" />
                초기화
              </button>
            )}
          </div>

          <div className="space-y-1.5">
            {categories.map((cat, idx) => {
              const isActive = activeAdminSubTab === cat.id;

              return (
                <div key={cat.id} className="relative group">
                  {editingCategoryId === cat.id ? (
                    <div className="flex items-center gap-1 p-1 bg-white border-2 border-indigo-500 rounded-xl shadow-xs">
                      <input
                        type="text"
                        value={tempLabel}
                        onChange={(e) => setTempLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveRename(cat.id);
                          if (e.key === 'Escape') setEditingCategoryId(null);
                        }}
                        className="w-full px-2 py-1 text-xs font-bold text-slate-800 focus:outline-none"
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveRename(cat.id)}
                        className="p-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                        title="저장"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setEditingCategoryId(null)}
                        className="p-1 rounded bg-slate-200 text-slate-600 hover:bg-slate-300"
                        title="취소"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setActiveAdminSubTab(cat.id)}
                        className={`flex-1 flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                          isActive
                            ? 'bg-indigo-600 text-white shadow-md font-black'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 bg-white border border-slate-200/80'
                        }`}
                      >
                        {renderIcon(cat.iconName)}
                        <span className="truncate">{cat.label}</span>
                      </button>

                      {isEditingCategories && (
                        <div className="flex items-center gap-0.5 bg-slate-200/70 p-1 rounded-xl">
                          <button
                            onClick={() => handleMoveCategory(idx, 'UP')}
                            disabled={idx === 0}
                            className="p-1 text-slate-600 hover:text-indigo-600 disabled:opacity-30 cursor-pointer"
                            title="위로 이동"
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleMoveCategory(idx, 'DOWN')}
                            disabled={idx === categories.length - 1}
                            className="p-1 text-slate-600 hover:text-indigo-600 disabled:opacity-30 cursor-pointer"
                            title="아래로 이동"
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleStartRename(cat)}
                            className="p-1 text-amber-700 hover:text-amber-900 cursor-pointer"
                            title="이름 수정"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Side Content Panel */}
        <div className="flex-1 min-w-0 bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
          {activeAdminSubTab === 'analytics' && <UsageAnalytics />}
          {activeAdminSubTab === 'users' && <UserManagement />}
          {activeAdminSubTab === 'org' && <OrganizationManagement />}
          {activeAdminSubTab === 'policy' && <LeavePolicyManagement />}
          {activeAdminSubTab === 'leave' && <LeaveApprovalManagement />}
          {activeAdminSubTab === 'mail' && <MailSystemSettings />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
